import React, { useMemo, useRef, useState, useEffect } from "react";
import { Tab, Nav, Row, Col } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import isEqual from "lodash/isEqual";
import {
  fetchInvoiceMonthClients,
  fetchInvoiceClientDetail,
  fetchInvoiceConfigurations,
  generateMonthlyInvoices,
  unpublishInvoiceClient,
  republishInvoiceClient,
  updateInvoiceClientWorkflowStatus,
  setActiveInvoiceMonth,
  setActiveInvoiceClient,
  setActiveInvoiceTab,
  setInvoiceTabNew,
  setInvoiceTabDirty,
  clearInvoiceTabValidationErrors,
  saveInvoiceClientTab,
} from "actions";
import { MainLoader } from "CommonComponent";
import "./stepper.css";
import LaborTab from "pages/Invoices/component/InvoicesUpdate/component/Labor/LaborTab/LaborTab";
import LaborRightContent from "pages/Invoices/component/InvoicesUpdate/component/Labor/LaborRightContent/LaborRightContent";
import TechnologyTab from "pages/Invoices/component/InvoicesUpdate/component/Technology/TechnologyTab/TechnologyTab";
import TechnologyRightContent from "pages/Invoices/component/InvoicesUpdate/component/Technology/TechnologyRightContent/TechnologyRightContent";
import FulfilmentTab from "pages/Invoices/component/InvoicesUpdate/component/Fulfilment/FulfilmentTab/FulfilmentTab";
import FulfilmentRightContent from "pages/Invoices/component/InvoicesUpdate/component/Fulfilment/FulfilmentRightContent/FulfilmentRightContent";
import MiscExpensesTab from "pages/Invoices/component/InvoicesUpdate/component/MiscExpenses/MiscExpensesTab/MiscExpensesTab";
import MiscRightContent from "pages/Invoices/component/InvoicesUpdate/component/MiscExpenses/MiscRightContent/MiscRightContent";
import InvoiceTab from "pages/Invoices/component/InvoicesUpdate/component/Invoice/InvoiceTab/InvoiceTab";
import MonthlyBilling from "pages/TimeSheetManage/Reports/MonthlyBilling";
import StatusDropdown from "pages/Invoices/component/InvoicesUpdate/component/StatusDropdown";
import Filter, {
  INVOICE_STATUS_FILTER_OPTIONS,
} from "pages/Invoices/component/Filter";

const invoiceTabs = [
  { id: "invoice", label: "Invoice" },
  { id: "labor", label: "Labor" },
  { id: "technology", label: "Technology" },
  { id: "fulfilment", label: "Fulfilment" },
  { id: "miscellaneous_expenses", label: "Miscellaneous Expenses" },
  { id: "all_hours", label: "All Hours" },
];

const MAX_PUBLISH_POLL_TICKS = 15;

const TAB_SECTION_OPTIONS = {
  labor: [
    "account_management",
    "supervisor",
    "supervisor_2",
    "rep_hours_onshore",
    "rep_hours_offshore",
    "crm_reporting_support",
    "it_support",
  ],
  technology: [
    "crm_subscription_annual",
    "telecom_support",
    "telecom_consumption",
    "misc_telecom",
  ],
  fulfilment: [
    "fulfillment",
    "fulfillment_misc",
    "fulfillment_tracker",
    "fulfillment_tracker_1",
    "supplies",
    "storage",
    "gift_cards",
    "fedex_expenses",
  ],
  miscellaneous_expenses: [
    "professional_voice_recording",
    "misc_telecom",
    "misc_credit",
  ],
  all_hours: [],
  invoice: [],
};

const ensureObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : {};

const unwrapRecord = (record) => {
  if (!record) return null;
  if (
    record?.data &&
    typeof record.data === "object" &&
    !Array.isArray(record.data)
  ) {
    return record.data;
  }
  return record;
};

const extractRecords = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  if (Array.isArray(payload?.data)) return payload.data;
  if (payload && typeof payload === "object") return [payload];
  return [];
};

const hasAnyValue = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === "object") return Object.keys(value).length > 0;
  return value !== undefined && value !== null && value !== "";
};

const mergeSectionValues = (defaults = {}, current = {}) => {
  const defaultMap = ensureObject(defaults);
  const currentMap = ensureObject(current);
  const keys = Array.from(
    new Set([...Object.keys(defaultMap), ...Object.keys(currentMap)]),
  );

  return keys.reduce((acc, key) => {
    const currentValue = currentMap[key];
    const defaultValue = defaultMap[key];
    acc[key] = hasAnyValue(currentValue) ? currentValue : defaultValue;
    return acc;
  }, {});
};

const isEnabledValue = (rawEnabled) =>
  rawEnabled === true ||
  rawEnabled === 1 ||
  rawEnabled === "1" ||
  rawEnabled === "true";

const pickFirstArray = (...candidates) => {
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate;
    }
  }

  return [];
};

const normalizeSectionStates = (sectionStates = [], tabId = "") => {
  if (!Array.isArray(sectionStates) || !tabId) return [];

  return sectionStates.filter((state) => {
    const stateTab = String(state?.tab || state?.tab_id || "");
    return stateTab === tabId;
  });
};

const migrateTelecomSectionIds = (sectionIds = []) => {
  if (!Array.isArray(sectionIds)) return [];

  const next = [];
  const seen = new Set();

  sectionIds.forEach((sectionId) => {
    const mappedIds =
      sectionId === "telecom"
        ? ["telecom_support", "telecom_consumption"]
        : [sectionId];

    mappedIds.forEach((id) => {
      if (!id || seen.has(id)) return;
      seen.add(id);
      next.push(id);
    });
  });

  return next;
};

const splitLegacyTelecomSections = (sections = {}) => {
  const safeSections = ensureObject(sections);
  const legacy = ensureObject(safeSections.telecom);

  if (Object.keys(legacy).length === 0) {
    return safeSections;
  }

  return {
    ...safeSections,
    ...(safeSections.telecom_support === undefined
      ? {
          telecom_support: {
            title: legacy.title,
            licenseRate: legacy.licenseRate,
            numberOfLicenses:
              legacy.numberOfLicenses ??
              legacy.noOfLicenses ??
              legacy.noOfLicense,
          },
        }
      : {}),
    ...(safeSections.telecom_consumption === undefined
      ? {
          telecom_consumption: {
            rate: legacy.rate ?? legacy.perMinuteRate,
            chargeTimeType: legacy.chargeTimeType || legacy.charge_time_type,
            chargeTimeUnit: legacy.chargeTimeUnit || legacy.charge_time_unit,
            handleTime:
              legacy.handleTime ??
              legacy.handle_time ??
              legacy.acdHandleTime,
            talkTime:
              legacy.talkTime ?? legacy.talk_time ?? legacy.acdTalkTime,
            totalMinutes: legacy.totalMinutes ?? legacy.total_minutes,
          },
        }
      : {}),
  };
};

const createAllSectionsSelected = () => ({
  labor: [...TAB_SECTION_OPTIONS.labor],
  technology: [...TAB_SECTION_OPTIONS.technology],
  fulfilment: [...TAB_SECTION_OPTIONS.fulfilment],
  miscellaneous_expenses: [...TAB_SECTION_OPTIONS.miscellaneous_expenses],
  all_hours: [],
  invoice: [],
});

const getTabSectionStatesFromRecord = (record, tabId) => {
  const tabs = ensureObject(record?.tabs);
  const tabSectionStates = normalizeSectionStates(
    pickFirstArray(
      tabs?.[tabId]?.section_states,
      tabs?.[tabId]?.sectionStates,
    ),
    tabId,
  );

  if (tabSectionStates.length > 0) {
    return tabSectionStates;
  }

  return normalizeSectionStates(
    pickFirstArray(record?.section_states, record?.sectionStates),
    tabId,
  );
};

const deriveSelectedSectionsFromRecord = (record, configRecord = null) => {
  const tabs = ensureObject(record?.tabs);
  const configTabs = ensureObject(configRecord?.tabs);
  const selected = createAllSectionsSelected();

  Object.entries(TAB_SECTION_OPTIONS).forEach(([tabId]) => {
    const enabledSectionsMap = ensureObject(tabs?.[tabId]?.enabled_sections);
    const tabSectionStates = getTabSectionStatesFromRecord(record, tabId);
    const sectionsData = ensureObject(tabs?.[tabId]?.sections);

    if (tabSectionStates.length > 0) {
      selected[tabId] = tabSectionStates
        .filter((state) => isEnabledValue(state?.is_enabled))
        .map((state) => String(state?.section_id || ""))
        .filter(Boolean);
      return;
    }

    if (Object.keys(enabledSectionsMap).length > 0) {
      selected[tabId] = Object.keys(enabledSectionsMap).filter((sectionId) =>
        isEnabledValue(enabledSectionsMap?.[sectionId]),
      );
      return;
    }

    if (configRecord) {
      const configEnabledSectionsMap = ensureObject(
        configTabs?.[tabId]?.enabled_sections,
      );
      const configTabSectionStates = getTabSectionStatesFromRecord(
        configRecord,
        tabId,
      );

      if (configTabSectionStates.length > 0) {
        selected[tabId] = configTabSectionStates
          .filter((state) => isEnabledValue(state?.is_enabled))
          .map((state) => String(state?.section_id || ""))
          .filter(Boolean);
        return;
      }

      if (Object.keys(configEnabledSectionsMap).length > 0) {
        selected[tabId] = Object.keys(configEnabledSectionsMap).filter(
          (sectionId) => isEnabledValue(configEnabledSectionsMap?.[sectionId]),
        );
        return;
      }
    }

    if (Object.keys(sectionsData).length > 0) {
      selected[tabId] = Object.keys(sectionsData);
      return;
    }
  });

  Object.keys(selected).forEach((tabId) => {
    selected[tabId] = migrateTelecomSectionIds(selected[tabId]);
  });

  return selected;
};

const normalizeTabNewFromRecord = (record) => {
  const tabs = ensureObject(record?.tabs);
  const directSections = ensureObject(record?.sections);
  const payloadSections = ensureObject(record?.payload?.sections);

  const pickFirstNonEmptyObject = (...candidates) => {
    for (const candidate of candidates) {
      const safeObject = ensureObject(candidate);
      if (Object.keys(safeObject).length > 0) {
        return safeObject;
      }
    }

    return {};
  };

  const normalizeInvoiceSections = (value, fallbackMonthKey = "") => {
    const safeValue = ensureObject(value);

    const directInvoiceSections = ensureObject(safeValue?.sections);
    if (Object.keys(directInvoiceSections).length > 0) {
      return directInvoiceSections;
    }

    const invoiceNode = ensureObject(safeValue?.invoice);
    if (Object.keys(invoiceNode).length > 0) {
      return {
        invoice: {
          ...invoiceNode,
          monthKey: invoiceNode?.monthKey || fallbackMonthKey,
        },
      };
    }

    if (Array.isArray(safeValue?.rows) || safeValue?.grandTotal !== undefined) {
      return {
        invoice: {
          monthKey: safeValue?.monthKey || fallbackMonthKey,
          rows: Array.isArray(safeValue?.rows) ? safeValue.rows : [],
          grandTotal: safeValue?.grandTotal ?? 0,
        },
      };
    }

    return {};
  };

  const invoiceSections = pickFirstNonEmptyObject(
    tabs?.invoice?.sections,
    normalizeInvoiceSections(tabs?.invoice),
    normalizeInvoiceSections(record?.invoice),
  );

  const sectionsSource =
    Object.keys(directSections).length > 0 ? directSections : payloadSections;

  const pickSections = (sectionIds = []) =>
    sectionIds.reduce((acc, sectionId) => {
      if (sectionsSource?.[sectionId] !== undefined) {
        acc[sectionId] = sectionsSource[sectionId];
      }
      return acc;
    }, {});

  const mergeWithFallback = (tabSections = {}, fallbackSectionIds = []) => {
    const safeTabSections = ensureObject(tabSections);
    if (Object.keys(safeTabSections).length > 0) {
      return safeTabSections;
    }

    return pickSections(fallbackSectionIds);
  };

  return {
    labor: mergeWithFallback(ensureObject(tabs?.labor?.sections), [
      "account_management",
      "supervisor",
      "supervisor_2",
      "rep_hours_onshore",
      "rep_hours_offshore",
      "crm_reporting_support",
      "it_support",
    ]),
    technology: splitLegacyTelecomSections(
      mergeWithFallback(ensureObject(tabs?.technology?.sections), [
        "crm_subscription_annual",
        "telecom_support",
        "telecom_consumption",
        "misc_telecom",
      ]),
    ),
    fulfilment: mergeWithFallback(ensureObject(tabs?.fulfilment?.sections), [
      "fulfillment",
      "fulfillment_misc",
      "fulfillment_tracker",
      "fulfillment_tracker_1",
      "supplies",
      "storage",
      "gift_cards",
      "fedex_expenses",
    ]),
    miscellaneous_expenses: mergeWithFallback(
      ensureObject(tabs?.miscellaneous_expenses?.sections),
      ["professional_voice_recording", "misc_telecom", "misc_credit"],
    ),
    all_hours: {},
    invoice: invoiceSections,
  };
};

const normalizeStringList = (values = []) => {
  if (!Array.isArray(values)) return [];

  return values.map((value) => String(value || "").trim()).filter(Boolean);
};

const dedupeStrings = (values = []) => {
  const seen = new Set();

  return values.filter((value) => {
    const key = String(value || "")
      .trim()
      .toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const firstArray = (...candidates) => {
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate;
    }
  }

  return [];
};

const toSectionLabel = (sectionId) =>
  String(sectionId || "")
    .replace(/^task_/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getCurrentMonthKey = () => {
  const date = new Date();
  const month = `${date.getMonth()}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}`;
};

const getInvoiceRecordStatus = (record = {}) => {
  return (
    record?.status ||
    record?.invoice_status ||
    record?.publish_status ||
    record?.state ||
    ""
  );
};

const normalizeInvoiceStatus = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const getInvoiceStatusLabel = (value) => {
  const status = normalizeInvoiceStatus(value);

  if (!status || status === "new") return "New";
  if (["in_progress", "in-progress"].includes(status)) return "In Progress";
  return status
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getProgramStatus = (record = {}) => {
  if (
    !Object.prototype.hasOwnProperty.call(record, "program_status") &&
    !Object.prototype.hasOwnProperty.call(record, "programStatus")
  ) {
    return null;
  }

  const raw = record?.program_status ?? record?.programStatus ?? null;
  if (raw === null || raw === undefined || raw === "") return null;

  return normalizeInvoiceStatus(raw);
};

const PROGRAM_STATUS_LABELS = {
  working: "Working",
  ready_to_review: "Ready to Review",
};

const getProgramStatusLabel = (programStatus, publishStatus) => {
  const normalizedProgramStatus = programStatus
    ? normalizeInvoiceStatus(programStatus)
    : null;

  if (
    (publishStatus === "new" || publishStatus === "unpublished") &&
    normalizedProgramStatus &&
    PROGRAM_STATUS_LABELS[normalizedProgramStatus]
  ) {
    return PROGRAM_STATUS_LABELS[normalizedProgramStatus];
  }

  return getInvoiceStatusLabel(publishStatus);
};

const getClientDisplayStatusKey = (client = {}) => {
  const publishStatus = normalizeInvoiceStatus(client?._status);
  const programStatus = client?._program_status
    ? normalizeInvoiceStatus(client._program_status)
    : null;

  if (
    (publishStatus === "new" || publishStatus === "unpublished" || !publishStatus) &&
    programStatus &&
    PROGRAM_STATUS_LABELS[programStatus]
  ) {
    return programStatus;
  }

  return publishStatus || "new";
};

const clientMatchesStatusFilter = (client, selectedStatusValues = []) => {
  if (
    !selectedStatusValues.length ||
    selectedStatusValues.includes("all")
  ) {
    return true;
  }

  return selectedStatusValues.includes(getClientDisplayStatusKey(client));
};

const buildMonthClientsParams = (selectedStatusValues = []) => {
  const statuses = (selectedStatusValues || []).filter(
    (value) => value && value !== "all",
  );

  if (!statuses.length) {
    return {};
  }

  return { status: statuses.join(",") };
};

const getActorLabel = (actor) => {
  if (!actor) return "";
  if (typeof actor === "string") return actor;

  return (
    actor?.name || actor?.full_name || actor?.display_name || actor?.email || ""
  );
};

const PUBLISHED_STATUSES = ["published", "publish"];
const REPUBLISHED_STATUSES = ["republished", "republish"];
const PROCESSING_STATUSES = [
  "processing",
  "publishing",
  "in_progress",
  "in-progress",
];

const isTruthyApiFlag = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

const getMergedClientRecord = (clientId, clientInvoiceById, clients) => {
  const detailRecord = ensureObject(clientInvoiceById?.[clientId]);
  const listRecord =
    clients.find(
      (client) => String(client?._id || "") === String(clientId || ""),
    ) || {};

  return { ...listRecord, ...detailRecord };
};

const isInvoiceReadonlyByRecord = (record = {}) => {
  const hasCanEditFlag =
    record?.can_edit !== undefined || record?.canEdit !== undefined;
  const hasReadonlyFlag =
    record?.is_readonly !== undefined ||
    record?.isReadonly !== undefined ||
    record?.readonly !== undefined;

  if (hasCanEditFlag || hasReadonlyFlag) {
    const canEdit =
      isTruthyApiFlag(record?.can_edit) || isTruthyApiFlag(record?.canEdit);
    const isReadonly =
      isTruthyApiFlag(record?.is_readonly) ||
      isTruthyApiFlag(record?.isReadonly) ||
      isTruthyApiFlag(record?.readonly);

    return !canEdit || isReadonly;
  }

  const status = normalizeInvoiceStatus(getInvoiceRecordStatus(record));
  const nonEditableStatuses = [
    ...PUBLISHED_STATUSES,
    ...REPUBLISHED_STATUSES,
    ...PROCESSING_STATUSES,
  ];

  if (status && nonEditableStatuses.includes(status)) return true;
  return false;
};

const canShowPublishMonthButton = (status) => {
  const normalized = normalizeInvoiceStatus(status);
  return ["new", "failed", ""].includes(normalized);
};

const canShowUnpublishButtonForStatus = (status) => {
  const normalized = normalizeInvoiceStatus(status);
  return (
    PUBLISHED_STATUSES.includes(normalized) ||
    REPUBLISHED_STATUSES.includes(normalized)
  );
};

const canShowRepublishButtonForStatus = (status) =>
  normalizeInvoiceStatus(status) === "unpublished";

const canShowWorkflowStatusDropdown = (status) => {
  const normalized = normalizeInvoiceStatus(status);
  return ["new", "unpublished", ""].includes(normalized);
};

const getInvoiceStatusMessage = (status, failedReason = "") => {
  const normalized = normalizeInvoiceStatus(status);

  if (["new", ""].includes(normalized)) {
    return "Invoice is in new. Publish when ready.";
  }
  if (normalized === "unpublished") {
    return "Invoice was unpublished. Make changes, then republish.";
  }
  if (normalized === "republished") {
    return "Invoice republished successfully.";
  }
  if (PROCESSING_STATUSES.includes(normalized)) {
    return "Publishing in progress…";
  }
  if (normalized === "published") {
    return "Invoice published successfully.";
  }
  if (normalized === "failed") {
    const reason = String(failedReason || "").trim();
    return reason
      ? `Publish failed: ${reason}. Fix and publish again.`
      : "Publish failed. Fix and publish again.";
  }

  return "";
};

const getInvoiceStatusBannerClass = (status) => {
  const normalized = normalizeInvoiceStatus(status);

  if (normalized === "failed" || normalized === "unpublished") {
    return "alert alert-danger mb-0";
  }
  if (normalized === "published") return "alert alert-success mb-0";
  if (PROCESSING_STATUSES.includes(normalized))
    return "alert alert-warning mb-0";
  return "alert alert-info mb-0";
};

const computeOverallMonthStatus = (clients = [], publishRun = null) => {
  const runStatus = normalizeInvoiceStatus(
    publishRun?.month_status || publishRun?.status || "",
  );

  if (PROCESSING_STATUSES.includes(runStatus)) {
    return runStatus;
  }

  const statuses = clients
    .map((client) => normalizeInvoiceStatus(client?._status))
    .filter(Boolean);

  const priority = [
    "processing",
    "publishing",
    "in_progress",
    "in-progress",
    "failed",
    "published",
    "republished",
    "unpublished",
    "new",
  ];

  for (const preferred of priority) {
    if (statuses.includes(preferred)) {
      return preferred;
    }
  }

  return "new";
};

const InvoicesUpdate = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const session = useSelector((state) => state.session);
  const invoicesState = useSelector((state) => state.invoicesState);
  const invoiceConfigurationState = useSelector(
    (state) => state.invoiceConfigurationState,
  );
  const object_viewed_id = session?.current_page_permissions?.object_id || "";

  const monthKey = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("month_key") || "";
  }, [location.search]);

  const [statusFilter, setStatusFilter] = useState([
    INVOICE_STATUS_FILTER_OPTIONS[0],
  ]);

  const selectedStatusValues = useMemo(
    () => (statusFilter || []).map((option) => option?.value).filter(Boolean),
    [statusFilter],
  );

  const isStatusFilterActive =
    selectedStatusValues.length > 0 && !selectedStatusValues.includes("all");

  const monthClientsParams = useMemo(
    () => buildMonthClientsParams(selectedStatusValues),
    [selectedStatusValues],
  );

  const allClients = useMemo(() => {
    const source = invoicesState?.monthClientsByKey?.[monthKey];

    if (!Array.isArray(source)) return [];

    return source.map((item, index) => {
      const id = String(
        item?.program_id || item?.programId || item?.id || index + 1,
      );
      const name =
        item?.program_name ||
        item?.programName ||
        item?.name ||
        `Program ${index + 1}`;
      const status = normalizeInvoiceStatus(
        item?.status ||
          item?.invoice_status ||
          item?.publish_status ||
          item?.state,
      );
      const programStatus = getProgramStatus(item);

      return {
        ...item,
        _id: id,
        _name: name,
        _status: status,
        _program_status: programStatus,
      };
    });
  }, [invoicesState?.monthClientsByKey, monthKey]);

  // Client-side match keeps UI correct if API status filtering is not live yet.
  const clients = useMemo(
    () =>
      allClients.filter((client) =>
        clientMatchesStatusFilter(client, selectedStatusValues),
      ),
    [allClients, selectedStatusValues],
  );

  const monthStatusClientsRef = useRef([]);
  useEffect(() => {
    if (!isStatusFilterActive && allClients.length > 0) {
      monthStatusClientsRef.current = allClients;
    }
  }, [allClients, isStatusFilterActive]);

  const hasMonthKey = Boolean(monthKey);
  const showNoClientsState =
    hasMonthKey &&
    !invoicesState?.loading &&
    clients.length === 0 &&
    !isStatusFilterActive;
  const showNoFilteredClientsState =
    hasMonthKey &&
    !invoicesState?.loading &&
    clients.length === 0 &&
    isStatusFilterActive;

  const activeClientFromStore =
    invoicesState?.activeClientIdByMonth?.[monthKey] || "";
  const activeTabByClient = invoicesState?.activeTabByClient || {};
  const stateTabNewByClient = invoicesState?.tabNewByClient || {};
  const stateClientInvoiceById = useMemo(
    () => invoicesState?.clientInvoiceById || {},
    [invoicesState?.clientInvoiceById],
  );
  const stateDirtyTabsByClient = invoicesState?.dirtyTabsByClient || {};
  const stateTabSavingByClient = invoicesState?.tabSavingByClient || {};
  const stateValidationErrorsByClientTab =
    invoicesState?.validationErrorsByClientTab || {};

  const [selectedSectionsByClient, setSelectedSectionsByClient] = useState({});

  const tabNewByClient = stateTabNewByClient;
  const clientInvoiceById = stateClientInvoiceById;
  const dirtyTabsByClient = stateDirtyTabsByClient;
  const tabSavingByClient = stateTabSavingByClient;
  const validationErrorsByClientTab = stateValidationErrorsByClientTab;
  const unsavedChangesMessage =
    "You have unsaved changes. Are you sure you want to exit without saving?";

  const [activeTab, setActiveTab] = useState(activeClientFromStore || "");
  const [refreshingClientId, setRefreshingClientId] = useState("");
  const [hasMoreTabs, setHasMoreTabs] = useState(false);
  const [hasTabsBefore, setHasTabsBefore] = useState(false);
  const [publishRun, setPublishRun] = useState(null);
  const [isStartingPublish, setIsStartingPublish] = useState(false);
  const [isPublishPolling, setIsPublishPolling] = useState(false);
  const [isTransitioningStatus, setIsTransitioningStatus] = useState(false);
  const [statusTransitionType, setStatusTransitionType] = useState("");
  const refreshRequestIdRef = useRef(0);
  const publishPollTicksRef = useRef(0);
  const initialClientSelectionByMonthRef = useRef({});
  const liveTabNewRef = useRef({});
  const monthClientsInFlightRef = useRef(new Set());
  const clientDetailInFlightRef = useRef(new Set());
  const configInFlightRef = useRef(new Set());
  const [showActions, setShowActions] = useState(false);


  const configRecord = useMemo(
    () => unwrapRecord(invoiceConfigurationState?.invoiceConfigurationRecord),
    [invoiceConfigurationState?.invoiceConfigurationRecord],
  );

  const configTabDefaults = useMemo(() => {
    const tabs = ensureObject(configRecord?.tabs);
    return {
      labor: ensureObject(tabs?.labor?.sections),
      technology: ensureObject(tabs?.technology?.sections),
      fulfilment: ensureObject(tabs?.fulfilment?.sections),
      miscellaneous_expenses: ensureObject(
        tabs?.miscellaneous_expenses?.sections,
      ),
    };
  }, [configRecord]);

  const showMainLoader = Boolean(
    invoicesState?.loading || invoiceConfigurationState?.loading,
  );

  // Ref for the scrollable container wrapping Nav
  const navScrollRef = useRef(null);
  const navRef = useRef(null);

  const activeInnerTabId =
    activeTabByClient?.[activeTab] || invoiceTabs?.[0]?.id || "labor";

  const activeClientRecord = useMemo(
    () => ensureObject(clientInvoiceById?.[activeTab]),
    [activeTab, clientInvoiceById],
  );

  const activeClientListRecord = useMemo(
    () =>
      clients.find(
        (client) => String(client?._id || "") === String(activeTab || ""),
      ) || {},
    [clients, activeTab],
  );

  const activeClientStatus =
    getInvoiceRecordStatus(activeClientRecord) ||
    getInvoiceRecordStatus(activeClientListRecord);
  const activeClientStatusKey = normalizeInvoiceStatus(
    activeClientStatus || publishRun?.status || "",
  );

  const activeMergedClientRecord = useMemo(
    () => getMergedClientRecord(activeTab, clientInvoiceById, clients),
    [activeTab, clientInvoiceById, clients],
  );

  const displayPublishStatus = activeClientStatusKey || "new";
  const displayMonthStatus = computeOverallMonthStatus(
    isStatusFilterActive && monthStatusClientsRef.current.length > 0
      ? monthStatusClientsRef.current
      : allClients,
    publishRun,
  );
  const displayMonthStatusLabel = getInvoiceStatusLabel(displayMonthStatus);
  const activeFailedReason =
    activeMergedClientRecord?.failed_reason ||
    activeMergedClientRecord?.failedReason ||
    "";
  const statusMessage = getInvoiceStatusMessage(
    displayPublishStatus,
    activeFailedReason,
  );
  const statusBannerClass = getInvoiceStatusBannerClass(displayPublishStatus);
  const canShowPublishButton = canShowPublishMonthButton(displayMonthStatus);
  const isMonthProcessingStatus =
    PROCESSING_STATUSES.includes(displayMonthStatus);

  const isClientReadonly = (clientId) =>
    isInvoiceReadonlyByRecord(
      getMergedClientRecord(clientId, clientInvoiceById, clients),
    );

  const hasUnsavedChanges = useMemo(
    () => Boolean(dirtyTabsByClient?.[activeTab]?.[activeInnerTabId]),
    [activeInnerTabId, activeTab, dirtyTabsByClient],
  );

  const confirmLeaveWithUnsavedChanges = (
    shouldConfirm = hasUnsavedChanges,
  ) => {
    if (!shouldConfirm) return true;
    return window.confirm(unsavedChangesMessage);
  };

  const baseRecordSectionsRef = useRef({});

  useEffect(() => {
    if (!monthKey) return;

    const statusKey = selectedStatusValues.slice().sort().join(",") || "all";
    const requestKey = `${monthKey}:${object_viewed_id}:${statusKey}`;
    if (monthClientsInFlightRef.current.has(requestKey)) {
      return;
    }

    monthClientsInFlightRef.current.add(requestKey);

    dispatch(setActiveInvoiceMonth(monthKey));
    dispatch(
      fetchInvoiceMonthClients(
        { monthKey, params: monthClientsParams },
        object_viewed_id,
      ),
    )
      .catch(() => {
        // Action handles snackbar/error state; avoid unhandled promise rejection.
      })
      .finally(() => {
        monthClientsInFlightRef.current.delete(requestKey);
      });
  }, [
    dispatch,
    monthKey,
    object_viewed_id,
    monthClientsParams,
    selectedStatusValues,
  ]);

  useEffect(() => {
    setSelectedSectionsByClient((prev) => {
      const selectedByClient = { ...prev };
      let changed = false;

      clients.forEach((client) => {
        const clientId = String(client?.program_id || client?.id || "");
        if (!clientId) return;

        const newSelected = deriveSelectedSectionsFromRecord(
          clientInvoiceById?.[clientId],
        );
        const newSelectedString = JSON.stringify(newSelected);

        if (baseRecordSectionsRef.current[clientId] !== newSelectedString) {
          baseRecordSectionsRef.current[clientId] = newSelectedString;
          selectedByClient[clientId] = newSelected;
          changed = true;
        }
      });

      return changed ? selectedByClient : prev;
    });
  }, [clients, clientInvoiceById]);

  useEffect(() => {
    if (!monthKey || clients.length === 0) {
      setActiveTab("");
      return;
    }

    const firstClientId = String(clients?.[0]?._id || "");
    const hasInitializedForMonth = Boolean(
      initialClientSelectionByMonthRef.current?.[monthKey],
    );

    if (!hasInitializedForMonth && firstClientId) {
      initialClientSelectionByMonthRef.current = {
        ...initialClientSelectionByMonthRef.current,
        [monthKey]: true,
      };
      setActiveTab(firstClientId);
      dispatch(setActiveInvoiceClient(monthKey, firstClientId));
      return;
    }

    setActiveTab((prev) => {
      if (prev && clients.some((c) => c._id === prev)) {
        return prev;
      }

      if (
        activeClientFromStore &&
        clients.some((c) => c._id === activeClientFromStore)
      ) {
        return String(activeClientFromStore);
      }

      return firstClientId;
    });

    const fallbackClientId =
      (activeClientFromStore &&
      clients.some((c) => c._id === activeClientFromStore)
        ? String(activeClientFromStore)
        : firstClientId) || "";

    if (fallbackClientId) {
      dispatch(setActiveInvoiceClient(monthKey, fallbackClientId));
    }
  }, [dispatch, monthKey, clients, activeClientFromStore]);

  useEffect(() => {
    if (!clients.length) return;

    setSelectedSectionsByClient((prev) => {
      const next = { ...(prev || {}) };
      let changed = false;

      clients.forEach((client) => {
        const clientId = String(client?._id || "");
        if (!clientId) return;
        if (!next[clientId]) {
          next[clientId] = createAllSectionsSelected();
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [clients]);

  useEffect(() => {
    if (!monthKey || !activeTab) return;

    if (clientInvoiceById?.[activeTab]) {
      return;
    }

    const requestKey = `${monthKey}:${activeTab}:${object_viewed_id}`;
    if (clientDetailInFlightRef.current.has(requestKey)) {
      return;
    }

    clientDetailInFlightRef.current.add(requestKey);

    dispatch(
      fetchInvoiceClientDetail(
        {
          monthKey,
          programId: activeTab,
          silent: true,
          showCommonLoader: true,
        },
        object_viewed_id,
      ),
    )
      .catch(() => {
        // Action handles error notification.
      })
      .finally(() => {
        clientDetailInFlightRef.current.delete(requestKey);
      });
  }, [dispatch, monthKey, activeTab, object_viewed_id, clientInvoiceById]);

  useEffect(() => {
    if (!activeTab) return;
    const invoiceRecord = clientInvoiceById?.[activeTab];
    if (!invoiceRecord) return;

    setSelectedSectionsByClient((prev) => {
      const derived = deriveSelectedSectionsFromRecord(
        invoiceRecord,
        configRecord,
      );
      const current = prev?.[activeTab];

      // Prevent infinite loop if already deep equal
      if (JSON.stringify(current) === JSON.stringify(derived)) {
        return prev;
      }
      return {
        ...(prev || {}),
        [activeTab]: derived,
      };
    });
  }, [activeTab, clientInvoiceById, configRecord]);

  useEffect(() => {
    if (!activeTab || !monthKey) return;

    const invoiceRecord = clientInvoiceById?.[activeTab] || {};
    const programId =
      invoiceRecord?.program_id ||
      clients.find((client) => client?._id === activeTab)?.program_id ||
      "";

    if (!programId) return;

    if (String(configRecord?.program_id || "") === String(programId)) return;

    const requestKey = `${programId}:${object_viewed_id}`;
    if (configInFlightRef.current.has(requestKey)) {
      return;
    }

    configInFlightRef.current.add(requestKey);

    dispatch(
      fetchInvoiceConfigurations(
        {
          program_id: programId,
          per_page: 1,
          page: 1,
        },
        object_viewed_id,
        { silent: true },
      ),
    )
      .then((res) => {
        const records = extractRecords(res?.data);
        if (records.length > 0) {
          dispatch({
            type: "INVOICE_CONFIG_GET_SUCCESS",
            record: records[0],
            actionType: "update",
          });
        }
      })
      .catch(() => {
        // Action handles error notification.
      })
      .finally(() => {
        configInFlightRef.current.delete(requestKey);
      });
  }, [
    dispatch,
    activeTab,
    monthKey,
    clientInvoiceById,
    clients,
    configRecord?.program_id,
    object_viewed_id,
  ]);

  useEffect(() => {
    if (!isPublishPolling) {
      return undefined;
    }

    const interval = setInterval(() => {
      if (!monthKey) return;

      publishPollTicksRef.current += 1;
      if (publishPollTicksRef.current > MAX_PUBLISH_POLL_TICKS) {
        setIsPublishPolling(false);
        setPublishRun(null);
        publishPollTicksRef.current = 0;
        return;
      }

      const monthRequestKey = `${monthKey}:${object_viewed_id}:publish-poll`;
      if (!monthClientsInFlightRef.current.has(monthRequestKey)) {
        monthClientsInFlightRef.current.add(monthRequestKey);
        dispatch(
          fetchInvoiceMonthClients(
            { monthKey, params: monthClientsParams, silent: true },
            object_viewed_id,
          ),
        )
          .catch(() => {})
          .finally(() => {
            monthClientsInFlightRef.current.delete(monthRequestKey);
          });
      }

      if (activeTab) {
        const clientRequestKey = `${monthKey}:${activeTab}:${object_viewed_id}`;
        if (!clientDetailInFlightRef.current.has(clientRequestKey)) {
          clientDetailInFlightRef.current.add(clientRequestKey);
          dispatch(
            fetchInvoiceClientDetail(
              {
                monthKey,
                programId: activeTab,
                silent: true,
              },
              object_viewed_id,
            ),
          )
            .catch(() => {})
            .finally(() => {
              clientDetailInFlightRef.current.delete(clientRequestKey);
            });
        }
      }
    }, 7000);

    return () => clearInterval(interval);
  }, [
    dispatch,
    isPublishPolling,
    monthKey,
    activeTab,
    object_viewed_id,
    monthClientsParams,
  ]);

  // Enable vertical scroll to scroll the horizontal tab bar
  useEffect(() => {
    const scrollEl = navScrollRef.current;
    if (!scrollEl) return;
    const onWheel = (e) => {
      e.preventDefault();
      scrollEl.scrollLeft += e.deltaY;
    };
    scrollEl.addEventListener("wheel", onWheel, { passive: false });
    return () => scrollEl.removeEventListener("wheel", onWheel);
  }, [hasMonthKey, clients]);

  // Track whether there are more tabs hidden to the right or left
  useEffect(() => {
    const scrollEl = navScrollRef.current;
    if (!scrollEl) return;
    const checkOverflow = () => {
      const atEnd =
        scrollEl.scrollLeft + scrollEl.clientWidth >= scrollEl.scrollWidth - 4;
      setHasMoreTabs(!atEnd && scrollEl.scrollWidth > scrollEl.clientWidth);
      setHasTabsBefore(scrollEl.scrollLeft > 4);
    };
    checkOverflow();
    scrollEl.addEventListener("scroll", checkOverflow);
    const ro = new ResizeObserver(checkOverflow);
    ro.observe(scrollEl);
    return () => {
      scrollEl.removeEventListener("scroll", checkOverflow);
      ro.disconnect();
    };
  }, [clients]);

  useEffect(() => {
    if (!hasUnsavedChanges) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = unsavedChangesMessage;
      return unsavedChangesMessage;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges, unsavedChangesMessage]);

  useEffect(() => {
    if (!hasUnsavedChanges) return undefined;

    window.history.pushState({ invoiceGuard: true }, "", window.location.href);

    const handlePopState = () => {
      if (window.confirm(unsavedChangesMessage)) {
        window.removeEventListener("popstate", handlePopState);
        window.history.back();
        return;
      }

      window.history.pushState(
        { invoiceGuard: true },
        "",
        window.location.href,
      );
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [hasUnsavedChanges, unsavedChangesMessage]);

  useEffect(() => {
    if (!hasUnsavedChanges) return undefined;

    const handleDocumentClick = (event) => {
      const anchor = event.target?.closest?.("a[href]");
      if (!anchor) return;

      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("#")) return;
      if (anchor.target === "_blank" || anchor.hasAttribute("download")) return;

      const nextUrl = new URL(href, window.location.origin);
      if (nextUrl.origin === "null") return;
      const currentUrl = new URL(window.location.href);
      if (nextUrl.href === currentUrl.href) return;

      if (!window.confirm(unsavedChangesMessage)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    document.addEventListener("click", handleDocumentClick, true);
    return () =>
      document.removeEventListener("click", handleDocumentClick, true);
  }, [hasUnsavedChanges, unsavedChangesMessage]);

  const handleClientTabChange = (clientId) => {
    const resolvedClientId = String(clientId || "");
    const isSameClient = resolvedClientId === activeTab;

    if (!resolvedClientId) {
      return;
    }

    if (!isSameClient && !confirmLeaveWithUnsavedChanges()) {
      return;
    }

    setActiveTab(resolvedClientId);

    if (monthKey && resolvedClientId) {
      dispatch(setActiveInvoiceClient(monthKey, resolvedClientId));

      if (!isSameClient) {
        const requestKey = `${monthKey}:${resolvedClientId}:${object_viewed_id}`;

        if (!clientDetailInFlightRef.current.has(requestKey)) {
          clientDetailInFlightRef.current.add(requestKey);

          dispatch(
            fetchInvoiceClientDetail(
              {
                monthKey,
                programId: resolvedClientId,
                silent: true,
                showCommonLoader: true,
              },
              object_viewed_id,
            ),
          )
            .catch(() => {
              // Action handles error notification.
            })
            .finally(() => {
              clientDetailInFlightRef.current.delete(requestKey);
            });
        }
      }

      if (isSameClient) {
        const requestId = refreshRequestIdRef.current + 1;
        refreshRequestIdRef.current = requestId;
        setRefreshingClientId(resolvedClientId);

        dispatch(
          fetchInvoiceClientDetail(
            {
              monthKey,
              programId: resolvedClientId,
              silent: true,
              showCommonLoader: true,
            },
            object_viewed_id,
          ),
        )
          .catch(() => {
            // Action handles error notification.
          })
          .finally(() => {
            if (refreshRequestIdRef.current === requestId) {
              setRefreshingClientId("");
            }
          });
      }
    }
  };

  const getVisibleTabsForClient = (clientId) => {
    if (
      isInvoiceReadonlyByRecord(
        getMergedClientRecord(clientId, clientInvoiceById, clients),
      )
    ) {
      return invoiceTabs.filter((tab) => tab.id === "invoice");
    }

    return invoiceTabs;
  };

  const getClientInnerTab = (clientId) => {
    const visibleTabs = getVisibleTabsForClient(clientId);
    const defaultTabId = visibleTabs?.[0]?.id || "invoice";
    const requestedTab =
      activeTabByClient?.[clientId] || invoiceTabs?.[0]?.id || "labor";

    if (visibleTabs.some((tab) => tab.id === requestedTab)) {
      return requestedTab;
    }

    return defaultTabId;
  };

  const handleInnerTabChange = (clientId, tabId) => {
    const resolvedTabId = String(tabId || "labor");
    const currentTabId =
      activeTabByClient?.[clientId] || invoiceTabs?.[0]?.id || "labor";
    const currentTabDirty = Boolean(
      dirtyTabsByClient?.[clientId]?.[currentTabId],
    );

    if (resolvedTabId === currentTabId) {
      return;
    }

    if (!confirmLeaveWithUnsavedChanges(currentTabDirty)) {
      return;
    }

    dispatch(setActiveInvoiceTab(clientId, resolvedTabId));
  };

  const getClientTabNew = (clientId, tabId) => {
    const fromStore = ensureObject(tabNewByClient?.[clientId]?.[tabId]);
    const fromRecord = ensureObject(
      normalizeTabNewFromRecord(clientInvoiceById?.[clientId])?.[tabId],
    );
    const resolvedNew =
      Object.keys(fromStore).length > 0 ? fromStore : fromRecord;

    return mergeSectionValues(
      String(configRecord?.program_id || "") ===
        String(
          clients.find((client) => client?._id === clientId)?.program_id || "",
        )
        ? configTabDefaults?.[tabId] || {}
        : {},
      resolvedNew,
    );
  };

  const getClientSelectedSections = (clientId, tabId) => {
    const selected = selectedSectionsByClient?.[clientId]?.[tabId];
    if (Array.isArray(selected)) return selected;

    const clientProgramId = String(
      clients.find((client) => client?._id === clientId)?.program_id || "",
    );
    const configProgramId = String(configRecord?.program_id || "");

    if (
      clientProgramId &&
      configProgramId &&
      clientProgramId === configProgramId
    ) {
      const fromConfig =
        deriveSelectedSectionsFromRecord(configRecord)?.[tabId];
      if (Array.isArray(fromConfig)) return fromConfig;
    }

    return TAB_SECTION_OPTIONS?.[tabId] || [];
  };

  const getClientTabValidationErrors = (clientId, tabId) => {
    const key = `${clientId}:${tabId}`;
    return validationErrorsByClientTab?.[key] || {};
  };

  const getFieldErrorText = (errors, field) => {
    const value = errors?.[field];
    if (Array.isArray(value)) return value[0] || "";
    if (typeof value === "string") return value;
    return "";
  };

  const getSectionFieldError = (
    clientId,
    tabId,
    sectionId,
    fieldName,
    rowIndex = null,
  ) => {
    const errors = getClientTabValidationErrors(clientId, tabId);
    if (!errors || !sectionId || !fieldName) return "";

    const oneBasedIndex = rowIndex === null ? null : rowIndex + 1;
    const directCandidates = [
      // Laravel-style invoice tab validation paths
      `payload.sections.${sectionId}.${fieldName}`,
      `sections.${sectionId}.${fieldName}`,
      `${sectionId}.${fieldName}`,
      `${sectionId}.payload.${fieldName}`,
      `payload.${sectionId}.${fieldName}`,
      ...(rowIndex === null
        ? []
        : [
            `payload.sections.${sectionId}.${rowIndex}.${fieldName}`,
            `payload.sections.${sectionId}.${oneBasedIndex}.${fieldName}`,
            `${sectionId}.${rowIndex}.${fieldName}`,
            `${sectionId}.${oneBasedIndex}.${fieldName}`,
            `${sectionId}[${rowIndex}].${fieldName}`,
            `${sectionId}[${oneBasedIndex}].${fieldName}`,
            `technology.${sectionId}.${rowIndex}.${fieldName}`,
            `technology.${sectionId}.${oneBasedIndex}.${fieldName}`,
            `technology.${sectionId}[${rowIndex}].${fieldName}`,
            `technology.${sectionId}[${oneBasedIndex}].${fieldName}`,
            `fulfilment.${sectionId}.${rowIndex}.${fieldName}`,
            `fulfilment.${sectionId}.${oneBasedIndex}.${fieldName}`,
            `fulfilment.${sectionId}[${rowIndex}].${fieldName}`,
            `fulfilment.${sectionId}[${oneBasedIndex}].${fieldName}`,
          ]),
      fieldName,
    ];

    for (const candidate of directCandidates) {
      const direct = getFieldErrorText(errors, candidate);
      if (direct) return direct;
    }

    const keyMatchesSection = (normalizedKey) =>
      normalizedKey === sectionId ||
      normalizedKey.startsWith(`${sectionId}.`) ||
      normalizedKey.includes(`.${sectionId}.`) ||
      normalizedKey.includes(`[${sectionId}]`);

    const keyMatchesField = (normalizedKey) =>
      normalizedKey === fieldName ||
      normalizedKey.endsWith(`.${fieldName}`) ||
      normalizedKey.endsWith(`[${fieldName}]`);

    for (const [key, value] of Object.entries(errors || {})) {
      const normalizedKey = String(key || "");
      const hasSection = keyMatchesSection(normalizedKey);
      const hasField = keyMatchesField(normalizedKey);
      const hasRow =
        rowIndex === null ||
        normalizedKey.includes(`.${rowIndex}.`) ||
        normalizedKey.includes(`.${oneBasedIndex}.`) ||
        normalizedKey.includes(`[${rowIndex}]`) ||
        normalizedKey.includes(`[${oneBasedIndex}]`);

      if (!hasSection || !hasField || !hasRow) {
        continue;
      }

      if (Array.isArray(value)) return value[0] || "";
      if (typeof value === "string") return value;
    }

    return "";
  };

  const getTabTopError = (clientId, tabId) => {
    const errors = getClientTabValidationErrors(clientId, tabId);
    const detailError = getFieldErrorText(errors, "detail");
    if (detailError) return detailError;

    const entries = Object.entries(errors || {}).filter(([, value]) => {
      if (Array.isArray(value)) return value.length > 0;
      return Boolean(value);
    });

    if (!entries.length) return "";
    const firstValue = entries[0][1];
    if (Array.isArray(firstValue)) return firstValue[0] || "";
    if (typeof firstValue === "string") return firstValue;
    return "Validation error found. Please review this tab.";
  };

  const handleTabSectionDataChange = (clientId, tabId, data) => {
    if (isClientReadonly(clientId)) {
      return;
    }

    if (tabId === "invoice") {
      return;
    }

    const nextData = ensureObject(data);
    const newKey = `${clientId}:${tabId}`;
    liveTabNewRef.current[newKey] = nextData;
    const currentData = ensureObject(tabNewByClient?.[clientId]?.[tabId]);

    if (isEqual(currentData, nextData)) {
      return;
    }

    dispatch(setInvoiceTabNew(clientId, tabId, nextData));
    dispatch(setInvoiceTabDirty(clientId, tabId, true));

    const currentErrors = getClientTabValidationErrors(clientId, tabId);
    if (Object.keys(currentErrors || {}).length > 0) {
      dispatch(clearInvoiceTabValidationErrors(clientId, tabId));
    }
  };

  const removeSectionDataFromNew = (clientId, tabId, sectionId) => {
    if (isClientReadonly(clientId)) {
      return;
    }

    const currentTabData = ensureObject(tabNewByClient?.[clientId]?.[tabId]);
    const nextTabData = { ...currentTabData };
    delete nextTabData[sectionId];
    const newKey = `${clientId}:${tabId}`;
    liveTabNewRef.current[newKey] = nextTabData;

    if (isEqual(currentTabData, nextTabData)) {
      return;
    }

    dispatch(setInvoiceTabNew(clientId, tabId, nextTabData));
    dispatch(setInvoiceTabDirty(clientId, tabId, true));

    const currentErrors = getClientTabValidationErrors(clientId, tabId);
    if (Object.keys(currentErrors || {}).length > 0) {
      dispatch(clearInvoiceTabValidationErrors(clientId, tabId));
    }
  };

  const handleTabSectionToggle = (clientId, tabId, sectionId) => {
    if (!clientId || !tabId || !sectionId) return;
    if (isClientReadonly(clientId)) return;

    const currentSelected = getClientSelectedSections(clientId, tabId);
    const hasSection = currentSelected.includes(sectionId);

    setSelectedSectionsByClient((prev) => {
      const clientSections = prev?.[clientId] || createAllSectionsSelected();
      const tabSections = Array.isArray(clientSections?.[tabId])
        ? clientSections[tabId]
        : [...(TAB_SECTION_OPTIONS?.[tabId] || [])];

      const nextTabSections = hasSection
        ? tabSections.filter((id) => id !== sectionId)
        : [...tabSections, sectionId];

      return {
        ...(prev || {}),
        [clientId]: {
          ...clientSections,
          [tabId]: nextTabSections,
        },
      };
    });

    if (hasSection) {
      removeSectionDataFromNew(clientId, tabId, sectionId);
    }

    dispatch(setInvoiceTabDirty(clientId, tabId, true));
  };

  const handleSaveTab = (client, tabId) => {
    const clientId = String(client?._id || "");
    if (!clientId || !tabId || !monthKey) return;
    if (tabId === "invoice") return;
    if (isClientReadonly(clientId)) return;

    const newKey = `${clientId}:${tabId}`;
    const latestLivePayload = ensureObject(liveTabNewRef.current?.[newKey]);
    const payload =
      Object.keys(latestLivePayload).length > 0
        ? latestLivePayload
        : ensureObject(getClientTabNew(clientId, tabId));
    const selectedSections = getClientSelectedSections(clientId, tabId);
    const allSectionIds = getTabSectionOptions(clientId, tabId).map(
      (option) => option.id,
    );
    const filteredPayload = Object.keys(payload).reduce((acc, key) => {
      if (selectedSections.includes(key)) {
        acc[key] = payload[key];
      }
      return acc;
    }, {});
    const invoiceRecord = clientInvoiceById?.[clientId] || {};
    const invoiceId =
      invoiceRecord?.invoice_id ||
      invoiceRecord?.id ||
      client?.invoice_id ||
      null;
    const configProgramId =
      client?.program_id ||
      invoiceRecord?.program_id ||
      configRecord?.program_id ||
      client?.program?.id ||
      null;

    dispatch(
      saveInvoiceClientTab(
        {
          invoiceId,
          monthKey,
          clientId,
          tabId,
          payload: filteredPayload,
          selectedSectionIds: selectedSections,
          allSectionIds,
          shouldSyncConfig: tabId !== "invoice",
          configProgramId,
        },
        object_viewed_id,
      ),
    ).catch(() => {
      // Action handles error notification and validation state.
    });
  };

  const getTabSectionOptions = (clientId, tabId) => {
    const clientRecord = ensureObject(clientInvoiceById?.[clientId]);
    const recordTabs = ensureObject(clientRecord?.tabs);
    const tabFromRecord = ensureObject(recordTabs?.[tabId]);
    const enabledSectionsMap = ensureObject(tabFromRecord?.enabled_sections);
    const tabSectionStates = normalizeSectionStates(
      pickFirstArray(
        tabFromRecord?.section_states,
        tabFromRecord?.sectionStates,
      ),
      tabId,
    );
    const sectionsData = ensureObject(tabFromRecord?.sections);
    const sectionLabels = ensureObject(tabFromRecord?.section_labels);

    const clientProgramId = String(
      clients.find((client) => client?._id === clientId)?.program_id || "",
    );
    const configProgramId = String(configRecord?.program_id || "");
    const isMatchedConfigProgram =
      clientProgramId && configProgramId && clientProgramId === configProgramId;

    const configTabs = ensureObject(configRecord?.tabs);
    const configTab = ensureObject(configTabs?.[tabId]);
    const configEnabledSectionsMap = ensureObject(configTab?.enabled_sections);
    const configTabSectionStates = normalizeSectionStates(
      pickFirstArray(configTab?.section_states, configTab?.sectionStates),
      tabId,
    );
    const configSectionsData = ensureObject(configTab?.sections);

    let keys = Object.keys(enabledSectionsMap);
    if (keys.length === 0 && tabSectionStates.length > 0) {
      keys = tabSectionStates
        .map((state) => String(state?.section_id || ""))
        .filter(Boolean);
    }
    if (keys.length === 0) {
      keys = Object.keys(sectionsData);
    }
    if (keys.length === 0 && isMatchedConfigProgram) {
      keys = Object.keys(configEnabledSectionsMap);
    }
    if (
      keys.length === 0 &&
      isMatchedConfigProgram &&
      configTabSectionStates.length > 0
    ) {
      keys = configTabSectionStates
        .map((state) => String(state?.section_id || ""))
        .filter(Boolean);
    }
    if (keys.length === 0 && isMatchedConfigProgram) {
      keys = Object.keys(configSectionsData);
    }
    if (keys.length === 0) {
      keys = TAB_SECTION_OPTIONS[tabId] || [];
    }

    keys = migrateTelecomSectionIds(dedupeStrings(keys));

    return keys.map((id) => ({
      id,
      label:
        sectionLabels?.[id] ||
        sectionsData?.[id]?.title ||
        configSectionsData?.[id]?.title ||
        toSectionLabel(id),
    }));
  };

  const refreshMonthAndActiveClient = () => {
    dispatch(
      fetchInvoiceMonthClients(
        { monthKey, params: monthClientsParams, silent: true },
        object_viewed_id,
      ),
    ).catch(() => {});

    if (activeTab) {
      dispatch(
        fetchInvoiceClientDetail(
          {
            monthKey,
            programId: activeTab,
            silent: true,
          },
          object_viewed_id,
        ),
      ).catch(() => {});
    }
  };

  const handleClientStatusTransition = (
    transitionType,
    clientId = activeTab,
  ) => {
    if (!monthKey || !clientId || isTransitioningStatus) return;

    const listRecord =
      clients.find(
        (client) => String(client?._id || "") === String(clientId || ""),
      ) || {};
    const invoiceRecord = ensureObject(clientInvoiceById?.[clientId]);
    const invoiceId =
      invoiceRecord?.invoice_id ||
      invoiceRecord?.id ||
      listRecord?.invoice_id ||
      listRecord?.id ||
      null;
    if (!invoiceId) return;

    setIsTransitioningStatus(true);
    setStatusTransitionType(transitionType);

    const action = (() => {
      if (transitionType === "unpublish") return unpublishInvoiceClient;
      if (transitionType === "republish") return republishInvoiceClient;
      if (["working", "ready_to_review"].includes(transitionType)) {
        return (params, objectViewedId) =>
          updateInvoiceClientWorkflowStatus(
            { ...params, workflowStatus: transitionType },
            objectViewedId,
          );
      }
      return null;
    })();

    if (!action) {
      setIsTransitioningStatus(false);
      setStatusTransitionType("");
      return;
    }

    dispatch(
      action(
        {
          invoiceId,
          monthKey,
          clientId,
        },
        object_viewed_id,
      ),
    )
      .then(() => {
        refreshMonthAndActiveClient();
      })
      .catch(() => {
        // Action handles snackbar/error state.
      })
      .finally(() => {
        setIsTransitioningStatus(false);
        setStatusTransitionType("");
      });
  };

  const handleGeneratePublish = () => {
    if (!monthKey || isStartingPublish || isMonthProcessingStatus) return;

    setIsStartingPublish(true);
    setIsPublishPolling(true);
    publishPollTicksRef.current = 0;
    dispatch(generateMonthlyInvoices({ monthKey }, object_viewed_id))
      .then((envelope) => {
        const run = ensureObject(envelope?.data);
        setPublishRun(run);
        refreshMonthAndActiveClient();
      })
      .catch(() => {
        // Action handles snackbar/error state.
        setIsPublishPolling(false);
        publishPollTicksRef.current = 0;
      })
      .finally(() => {
        setIsStartingPublish(false);
      });
  };

  useEffect(() => {
    if (!isPublishPolling) return;

    const status = String(displayMonthStatus || "").toLowerCase();
    if (status && !PROCESSING_STATUSES.includes(status)) {
      setIsPublishPolling(false);
      publishPollTicksRef.current = 0;
      setPublishRun(null);
    }
  }, [displayMonthStatus, isPublishPolling]);

  const renderInnerTabContent = (clientId, tabId) => {
    const selectedSections = getClientSelectedSections(clientId, tabId);
    const initialSectionData = getClientTabNew(clientId, tabId);
    const sectionOptions = getTabSectionOptions(clientId, tabId);

    const getFieldError = (sectionId, fieldName, rowIndex = null) =>
      getSectionFieldError(clientId, tabId, sectionId, fieldName, rowIndex);

    if (tabId === "labor") {
      return (
        <LaborTab
          key={`${clientId}-${tabId}`}
          selectedSections={selectedSections}
          sectionOptions={sectionOptions}
          initialSectionData={initialSectionData}
          getFieldError={getFieldError}
          onSectionDataChange={(data) =>
            handleTabSectionDataChange(clientId, tabId, data)
          }
        />
      );
    }

    if (tabId === "technology") {
      return (
        <TechnologyTab
          key={`${clientId}-${tabId}`}
          selectedSections={selectedSections}
          sectionOptions={sectionOptions}
          initialSectionData={initialSectionData}
          getFieldError={getFieldError}
          onSectionDataChange={(data) =>
            handleTabSectionDataChange(clientId, tabId, data)
          }
        />
      );
    }

    if (tabId === "fulfilment") {
      const clientRecord = ensureObject(clientInvoiceById?.[clientId]);
      const clientMeta =
        clients.find(
          (client) => String(client?._id || "") === String(clientId || ""),
        ) || {};
      const newFulfillmentSection = ensureObject(
        initialSectionData?.fulfillment,
      );
      const tabFulfilmentSections = ensureObject(
        clientRecord?.tabs?.fulfilment?.sections,
      );
      const fulfillmentSection = ensureObject(
        tabFulfilmentSections?.fulfillment,
      );

      const postageRows = firstArray(
        newFulfillmentSection?.postage_rows,
        newFulfillmentSection?.postage_data,
        fulfillmentSection?.postage_rows,
        fulfillmentSection?.postage_data,
        clientRecord?.postage_rows,
        clientRecord?.postageRows,
        clientRecord?.postage_data,
        clientRecord?.postageData,
        clientRecord?.postage?.rows,
        clientRecord?.postage?.items,
        clientRecord?.postage,
        clientMeta?.postage_rows,
        clientMeta?.postage_data,
      );

      const programBrands = dedupeStrings([
        ...normalizeStringList(newFulfillmentSection?.program_brands),
        ...normalizeStringList(newFulfillmentSection?.brand_names),
        ...normalizeStringList(newFulfillmentSection?.brands),
        ...normalizeStringList(fulfillmentSection?.program_brands),
        ...normalizeStringList(fulfillmentSection?.brand_names),
        ...normalizeStringList(fulfillmentSection?.brands),
        ...normalizeStringList(clientRecord?.program_brands),
        ...normalizeStringList(clientRecord?.brand_names),
        ...normalizeStringList(clientRecord?.brands),
        ...normalizeStringList(clientRecord?.postage?.brands),
        ...normalizeStringList(clientMeta?.program_brands),
        ...normalizeStringList(clientMeta?.brand_names),
        ...normalizeStringList(clientMeta?.brands),
      ]);

      return (
        <FulfilmentTab
          key={`${clientId}-${tabId}`}
          selectedSections={selectedSections}
          sectionOptions={sectionOptions}
          invoiceMonthKey={monthKey}
          postageRows={postageRows}
          programBrands={programBrands}
          initialSectionData={initialSectionData}
          getFieldError={getFieldError}
          onSectionDataChange={(data) =>
            handleTabSectionDataChange(clientId, tabId, data)
          }
        />
      );
    }

    if (tabId === "miscellaneous_expenses") {
      return (
        <MiscExpensesTab
          key={`${clientId}-${tabId}`}
          selectedSections={selectedSections}
          sectionOptions={sectionOptions}
          initialSectionData={initialSectionData}
          getFieldError={getFieldError}
          onSectionDataChange={(data) =>
            handleTabSectionDataChange(clientId, tabId, data)
          }
        />
      );
    }

    if (tabId === "invoice") {
      const laborData = getClientTabNew(clientId, "labor");
      const technologyData = getClientTabNew(clientId, "technology");
      const fulfilmentData = getClientTabNew(clientId, "fulfilment");
      const miscData = getClientTabNew(clientId, "miscellaneous_expenses");
      const invoiceData = getClientTabNew(clientId, "invoice");
      const mergedClientRecord = getMergedClientRecord(
        clientId,
        clientInvoiceById,
        clients,
      );
      const clientStatus = normalizeInvoiceStatus(
        getInvoiceRecordStatus(mergedClientRecord),
      );
      const publishFileUrl =
        mergedClientRecord?.publish_file_url ||
        mergedClientRecord?.publishFileUrl ||
        "";

      return (
        <InvoiceTab
          key={`${clientId}-${tabId}`}
          monthKey={monthKey}
          projectName={
            mergedClientRecord?._name || mergedClientRecord?.program_name || ""
          }
          clientName={
            mergedClientRecord?.client_name ||
            mergedClientRecord?.clientName ||
            ""
          }
          invoiceStatus={clientStatus}
          publishFileUrl={
            ["published", "republished"].includes(clientStatus)
              ? publishFileUrl
              : ""
          }
          laborData={laborData}
          technologyData={technologyData}
          fulfilmentData={fulfilmentData}
          miscData={miscData}
          initialSectionData={invoiceData}
          onSectionDataChange={undefined}
          selectedSectionsByTab={{
            labor: getClientSelectedSections(clientId, "labor"),
            technology: getClientSelectedSections(clientId, "technology"),
            fulfilment: getClientSelectedSections(clientId, "fulfilment"),
            miscellaneous_expenses: getClientSelectedSections(
              clientId,
              "miscellaneous_expenses",
            ),
          }}
        />
      );
    }

    if (tabId === "all_hours") {
      if (getClientInnerTab(clientId) !== "all_hours") {
        return null;
      }

      const invoiceClientMeta =
        clients.find((c) => String(c?._id || "") === String(clientId || "")) ||
        {};
      const fixedProgramIds = [String(invoiceClientMeta?._id || "")].filter(
        Boolean,
      );

      return (
        <MonthlyBilling
          embeddedView
          hideExportActions
          invoiceMode
          fixedMonthKey={monthKey}
          fixedProgramIds={fixedProgramIds}
        />
      );
    }

    return null;
  };

  const renderRightTabContent = (clientId, tabId) => {
    const selectedSections = getClientSelectedSections(clientId, tabId);
    const sectionOptions = getTabSectionOptions(clientId, tabId);
    const onToggleSection = (sectionId) =>
      handleTabSectionToggle(clientId, tabId, sectionId);

    if (tabId === "labor") {
      return (
        <LaborRightContent
          selectedSections={selectedSections}
          sectionOptions={sectionOptions}
          onToggleSection={onToggleSection}
        />
      );
    }

    if (tabId === "technology") {
      return (
        <TechnologyRightContent
          selectedSections={selectedSections}
          sectionOptions={sectionOptions}
          onToggleSection={onToggleSection}
        />
      );
    }

    if (tabId === "fulfilment") {
      return (
        <FulfilmentRightContent
          selectedSections={selectedSections}
          sectionOptions={sectionOptions}
          onToggleSection={onToggleSection}
        />
      );
    }

    if (tabId === "miscellaneous_expenses") {
      return (
        <MiscRightContent
          selectedSections={selectedSections}
          sectionOptions={sectionOptions}
          onToggleSection={onToggleSection}
        />
      );
    }

    return null;
  };

  // Move tabs by scrolling to the next/prev tab element
  const moveTabs = (direction) => {
    if (!clients.length) return;
    if (!navRef.current) return;
    const navItems = navRef.current.querySelectorAll(".nav-link");
    const activeIdx = clients.findIndex((client) => client._id === activeTab);
    let targetIdx = activeIdx;
    if (direction === "next" && activeIdx < clients.length - 1) {
      targetIdx = activeIdx + 1;
    } else if (direction === "prev" && activeIdx > 0) {
      targetIdx = activeIdx - 1;
    }
    if (navItems[targetIdx]) {
      navItems[targetIdx].scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
      handleClientTabChange(clients[targetIdx]?._id || "");
    }
  };

  return (
    <div className="wrapper-div">
      {showMainLoader && <MainLoader />}
      <div className="d-flex align-items-center justify-content-between px-3 pt-3 mb-4">
        <h3 className="font-24 font-weight-bold heading-color d-flex align-items-center gap-2">
          <button
            type="button"
            className="border-0 bg-transparent p-0 d-flex align-items-center"
            onClick={() => {
              if (!confirmLeaveWithUnsavedChanges()) {
                return;
              }

              navigate("/invoices");
            }}
            aria-label="Back to invoices"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M9.57 5.92969L3.5 11.9997L9.57 18.0697"
                stroke="#292D32"
                strokeWidth="2"
                strokeMiterlimit="10"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M20.4999 12H3.66992"
                stroke="#292D32"
                strokeWidth="2"
                strokeMiterlimit="10"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          {monthKey || "Current Month"}
          {""}
        </h3>
        <div>
              {hasMonthKey && clients.length > 0 && statusMessage ? (
                    <div className="px-3 mb-3">
                      <div className={statusBannerClass} role="status" style={{fontSize:'12px', padding:'4px'}}>
                        {statusMessage}
                      </div>
                    </div>
                  ) : null}
            </div>
        <div className="d-flex align-items-center gap-2">
          {hasMonthKey ? (
            <Filter
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
            />
          ) : null}
          <div className="d-flex align-items-center justify-content-between gap-2">
            <span className="font-12 heading-color">
              {`Month Status: ${displayMonthStatusLabel}`}
            </span>
            {canShowPublishButton ? (
              <button
                className="n-u-btn"
                type="button"
                disabled={
                  isStartingPublish ||
                  isTransitioningStatus ||
                  isMonthProcessingStatus
                }
                onClick={handleGeneratePublish}
              >
                {isStartingPublish ? "Publishing..." : "Publish month"}
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {!hasMonthKey && (
        <div className="px-3">
          <div className="invoice-section-div text-center">
            <p className="m-0">
              Month is missing. Please select a month from the invoices list.
            </p>
          </div>
        </div>
      )}

      {showNoClientsState && (
        <div className="px-3">
          <div className="invoice-section-div text-center">
            <p className="m-0">No programs found for this month.</p>
          </div>
        </div>
      )}

      {showNoFilteredClientsState && (
        <div className="px-3">
          <div className="invoice-section-div text-center">
            <p className="m-0">
              No programs match the selected status filter.
            </p>
          </div>
        </div>
      )}

      <div className="px-3 mt-3">
        {hasMonthKey && clients.length > 0 ? (
          <div className="view-container p-2">
            <Tab.Container
              activeKey={activeTab}
              onSelect={handleClientTabChange}
              mountOnEnter
              unmountOnExit
            >
              <Row>
                <Col lg={12}>
                  <div
                    className="clients-name-section d-flex justify-content-between align-items-center"
                    style={{ overflow: "hidden" }}
                  >
                    <img
                      src="/assets/images/arrow-previous.svg"
                      alt="Previous"
                      style={{
                        cursor: "pointer",
                        background: "rgba(40, 36, 95, 0.11)",
                        padding: "10px",
                        borderRadius: "50%",
                        marginRight: "8px",
                      }}
                      onClick={() => moveTabs("prev")}
                    />
                    <div
                      style={{
                        flex: 1,
                        overflowX: "auto",
                        overflowY: "hidden",
                        position: "relative",
                        scrollbarWidth: "none", // Firefox
                        msOverflowStyle: "none", // IE/Edge
                      }}
                      ref={navScrollRef}
                      className="tabbar-hide-scroll"
                    >
                      {hasTabsBefore && (
                        <div
                          style={{
                            position: "absolute",
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: 100,
                            background:
                              "linear-gradient(to left, transparent 0%, rgba(244,245,245,0.6) 30%, rgba(244,245,245,0.92) 60%, #F4F5F5 100%)",
                            backdropFilter: "blur(0.2px)",
                            WebkitBackdropFilter: "blur(0.2px)",
                            pointerEvents: "none",
                            zIndex: 2,
                          }}
                        />
                      )}
                      {hasMoreTabs && (
                        <div
                          style={{
                            position: "absolute",
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: 100,
                            background:
                              "linear-gradient(to right, transparent 0%, rgba(244,245,245,0.6) 30%, rgba(244,245,245,0.92) 60%, #F4F5F5 100%)",
                            backdropFilter: "blur(0.2px)",
                            WebkitBackdropFilter: "blur(0.2px)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            paddingRight: 10,
                            pointerEvents: "none",
                            zIndex: 2,
                            fontWeight: "700",
                            fontSize: 18,
                            color: "#6c757d",
                            letterSpacing: 2,
                          }}
                        >
                          {/* ... */}
                        </div>
                      )}
                      <Nav
                        ref={navRef}
                        variant="tabs"
                        className="flex-nowrap"
                        style={{
                          display: "flex",
                          transition: "none",
                          whiteSpace: "nowrap",
                          marginBottom: 0,
                        }}
                      >
                        {clients.map((client) => (
                          <Nav.Item
                            key={client._id}
                            style={{ display: "inline-block" }}
                          >
                            <Nav.Link
                              eventKey={client._id}
                              className={
                                client?._status === "unpublished"
                                  ? "invoice-client-tab-unpublished"
                                  : ""
                              }
                              style={{
                                display: "inline-block",
                                width: "auto",
                                minWidth: 0,
                                padding: "7px 18px",
                              }}
                            >
                              {client._name}
                            </Nav.Link>
                          </Nav.Item>
                        ))}
                      </Nav>
                    </div>
                    <img
                      src="/assets/images/arrow-next.svg"
                      alt="Next"
                      style={{
                        cursor: "pointer",
                        background: "rgba(40, 36, 95, 0.11)",
                        padding: "10px",
                        borderRadius: "50%",
                        marginLeft: "8px",
                      }}
                      onClick={() => moveTabs("next")}
                    />
                  </div>
                  {refreshingClientId && refreshingClientId === activeTab ? (
                    <div className="font-12 text-muted mt-1">Refreshing...</div>
                  ) : null}
                </Col>
              </Row>
              <Tab.Content>
                {clients.map((client) => {
                  const clientMergedRecord = getMergedClientRecord(
                    client._id,
                    clientInvoiceById,
                    clients,
                  );
                  const clientPublishStatus =
                    normalizeInvoiceStatus(
                      getInvoiceRecordStatus(clientMergedRecord),
                    ) || "new";
                  const clientProgramStatus =
                    getProgramStatus(clientMergedRecord);
                  const clientDisplayStatusLabel = getProgramStatusLabel(
                    clientProgramStatus,
                    clientPublishStatus,
                  );
                  const showClientWorkflowDropdown =
                    canShowWorkflowStatusDropdown(clientPublishStatus) &&
                    !isClientReadonly(client._id);
                  const showClientUnpublishButton =
                    canShowUnpublishButtonForStatus(clientPublishStatus);
                  const showClientRepublishButton =
                    canShowRepublishButtonForStatus(clientPublishStatus);
                  const isClientProcessingStatus =
                    PROCESSING_STATUSES.includes(clientPublishStatus);
                  const clientRecord = ensureObject(
                    clientInvoiceById?.[client._id],
                  );
                  const clientLastActionBy =
                    getActorLabel(clientRecord?.last_action_by) ||
                    getActorLabel(clientRecord?.updated_by) ||
                    getActorLabel(client?.last_action_by) ||
                    getActorLabel(client?.updated_by) ||
                    "";
                  const clientLastActionAt =
                    clientRecord?.last_action_at ||
                    clientRecord?.updated_at ||
                    client?.last_action_at ||
                    client?.updated_at ||
                    "";

                  return (
                    <Tab.Pane eventKey={client._id} key={client._id}>
                      <Row>
                        <Col lg={12}>
                          <div className="invoice-tabs">
                            <Tab.Container
                              activeKey={getClientInnerTab(client._id)}
                              onSelect={(tabId) =>
                                handleInnerTabChange(client._id, tabId)
                              }
                              mountOnEnter
                              unmountOnExit
                            >
                              <div className="p-3 border-bottom invoice-inner-tab-bar">
                                <Nav
                                  variant="tabs"
                                  className="flex-wrap invoice-inner-tab-nav"
                                >
                                  {getVisibleTabsForClient(client._id).map(
                                    (tab) => (
                                      <Nav.Item key={`${client._id}-${tab.id}`}>
                                        <Nav.Link eventKey={tab.id}>
                                          {tab.label}
                                        </Nav.Link>
                                      </Nav.Item>
                                    ),
                                  )}
                                </Nav>
                                <div className="invoice-inner-tab-actions d-flex align-items-center flex-wrap justify-content-end" style={{gap:'180px'}}>
                                  {getClientInnerTab(client._id) !==
                                    "invoice" &&
                                  getClientInnerTab(client._id) !==
                                    "all_hours" ? (
                                    <button
                                      type="button"
                                      className="tabs-save-btn-inline n-u-btn"
                                      disabled={
                                        isClientReadonly(client._id) ||
                                        !dirtyTabsByClient?.[client._id]?.[
                                          getClientInnerTab(client._id)
                                        ] ||
                                        Boolean(
                                          tabSavingByClient?.[client._id]?.[
                                            getClientInnerTab(client._id)
                                          ],
                                        )
                                      }
                                      onClick={() =>
                                        handleSaveTab(
                                          client,
                                          getClientInnerTab(client._id),
                                        )
                                      }
                                    >
                                      {tabSavingByClient?.[client._id]?.[
                                        getClientInnerTab(client._id)
                                      ]
                                        ? "Saving..."
                                        : "Save Tab"}
                                    </button>
                                  ) : null}
                                  <div className="invoice-program-status-controls d-flex flex-column align-items-end gap-1">
                                    <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
                                      {/* Status always visible */}
                                      <span
                                        className={`invoice-program-status-badge${
                                          clientPublishStatus === "unpublished"
                                            ? " unpublished"
                                            : ""
                                        }`}
                                      >
                                        {`Program: ${clientDisplayStatusLabel}`}
                                      </span>

                                      {/* Dropdown Trigger */}
                                      <div className="position-relative">
                                        <button
                                          type="button"
                                          style={{
                                            backgroundColor: '#EBF5EB',
                                            border: '1px solid #46A447',
                                            borderRadius: '11px',
                                            padding: '4px 8px',
                                            fontSize: '12px', 
                                          }}
                                          onClick={() =>
                                            setShowActions(!showActions)
                                          }
                                        >
                                          <img
                                            src="/assets/images/dropdown-icon.svg"
                                            alt="Actions"
                                          />
                                        </button>

                                        {showActions && (
                                          <div
                                            className="position-absolute end-0 mt-2 bg-white shadow rounded p-3"
                                            style={{
                                              minWidth: "250px",
                                              zIndex: 1000,
                                            }}
                                          >
                                            
                                            {/* Workflow Status Dropdown */}
                                            {showClientWorkflowDropdown && (
                                              <div className="mb-2 border-bottom border-2 pb-2">
                                                <StatusDropdown
                                                  value={
                                                    clientProgramStatus || ""
                                                  }
                                                  onSelect={(nextStatus) =>
                                                    handleClientStatusTransition(
                                                      nextStatus,
                                                      client._id,
                                                    )
                                                  }
                                                  disabled={
                                                    isClientReadonly(
                                                      client._id,
                                                    ) ||
                                                    isTransitioningStatus ||
                                                    isClientProcessingStatus ||
                                                    isStartingPublish
                                                  }
                                                  isTransitioning={
                                                    isTransitioningStatus
                                                  }
                                                  transitionType={
                                                    statusTransitionType
                                                  }
                                                />
                                              </div>
                                            )}

                                            {/* Unpublish */}
                                            {showClientUnpublishButton && (
                                              <button
                                                className="n-u-btn w-100 mb-2"
                                                type="button"
                                                disabled={
                                                  isTransitioningStatus ||
                                                  isClientProcessingStatus ||
                                                  isStartingPublish
                                                }
                                                onClick={() =>
                                                  handleClientStatusTransition(
                                                    "unpublish",
                                                    client._id,
                                                  )
                                                }
                                              >
                                                {isTransitioningStatus &&
                                                statusTransitionType ===
                                                  "unpublish"
                                                  ? "Unpublishing program..."
                                                  : "Unpublish program"}
                                              </button>
                                            )}

                                            {/* Republish */}
                                            {showClientRepublishButton && (
                                              <button
                                                className="n-u-btn w-100"
                                                type="button"
                                                disabled={
                                                  isTransitioningStatus ||
                                                  isClientProcessingStatus ||
                                                  isStartingPublish
                                                }
                                                onClick={() =>
                                                  handleClientStatusTransition(
                                                    "republish",
                                                    client._id,
                                                  )
                                                }
                                              >
                                                {isTransitioningStatus &&
                                                statusTransitionType ===
                                                  "republish"
                                                  ? "Republishing program..."
                                                  : "Republish program"}
                                              </button>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>

                                    <span className="font-12 text-muted invoice-status-meta">
                                      {clientLastActionBy || clientLastActionAt
                                        ? `Last action${
                                            clientLastActionBy
                                              ? ` by ${clientLastActionBy}`
                                              : ""
                                          }${
                                            clientLastActionAt
                                              ? ` at ${clientLastActionAt}`
                                              : ""
                                          }`
                                        : ""}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              <Tab.Content className="position-relative">
                                {getVisibleTabsForClient(client._id).map(
                                  (tab) => (
                                    <Tab.Pane
                                      key={`${client._id}-${tab.id}-pane`}
                                      eventKey={tab.id}
                                    >
                                      <div className="d-flex align-items-center justify-content-between mb-3">
                                        <div className="text-danger font-12">
                                          {getTabTopError(client._id, tab.id)}
                                        </div>
                                      </div>
                                      <Row>
                                        <Col
                                          lg={
                                            tab.id === "invoice" ||
                                            tab.id === "all_hours"
                                              ? 12
                                              : 9
                                          }
                                        >
                                          <div
                                            className={`invoice-tabs-content${tab.id !== "invoice" ? " border-end pe-3" : ""}`}
                                            style={
                                              tab.id !== "invoice" &&
                                              tab.id !== "all_hours" &&
                                              isClientReadonly(client._id)
                                                ? {
                                                    pointerEvents: "none",
                                                    opacity: 0.65,
                                                  }
                                                : undefined
                                            }
                                          >
                                            {renderInnerTabContent(
                                              client._id,
                                              tab.id,
                                            )}
                                          </div>
                                        </Col>
                                        {tab.id !== "invoice" &&
                                          tab.id !== "all_hours" && (
                                            <Col lg={3}>
                                              <div
                                                className="invoice-tabs-content ps-3"
                                                style={
                                                  isClientReadonly(client._id)
                                                    ? {
                                                        pointerEvents: "none",
                                                        opacity: 0.65,
                                                      }
                                                    : undefined
                                                }
                                              >
                                                {renderRightTabContent(
                                                  client._id,
                                                  tab.id,
                                                )}
                                              </div>
                                            </Col>
                                          )}
                                      </Row>
                                    </Tab.Pane>
                                  ),
                                )}
                              </Tab.Content>
                            </Tab.Container>
                          </div>
                        </Col>
                      </Row>
                    </Tab.Pane>
                  );
                })}
              </Tab.Content>
            </Tab.Container>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default InvoicesUpdate;
